/**
 * Menu navigation for keyboard + gamepad: spatial focus movement between `[data-nav]`
 * elements of the top-most layer, activate (Enter/Space/A), back (Esc/B), and left/right
 * adjustment of sliders/segmented controls. Mouse hover moves focus too.
 */
import { el } from './dom';

export interface NavLayer {
  root: HTMLElement;
  onBack?: () => void;
  /** Gamepad Start pressed on this layer. */
  onStart?: () => void;
}

type Dir = 'up' | 'down' | 'left' | 'right';

function visible(e: Element): boolean {
  if (!(e as HTMLElement).isConnected) return false;
  const r = e.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return false;
  const cs = getComputedStyle(e);
  return cs.visibility !== 'hidden' && !(e as HTMLButtonElement).disabled;
}

export class MenuNav {
  private layers: NavLayer[] = [];
  private focused?: HTMLElement;
  private padPrev: boolean[] = [];
  private padRepeat = { dir: '' as Dir | '', next: 0 };
  private rafId = 0;
  /** Set while gameplay owns input (nav ignores keys). */
  suspended = false;
  onMove?: () => void;
  onActivate?: () => void;
  /** Fired when a gamepad is used (to swap HUD glyphs). */
  onGamepad?: () => void;

  constructor() {
    window.addEventListener('keydown', this.onKey, true);
    document.addEventListener('pointerover', (e) => {
      const t = (e.target as HTMLElement).closest?.('[data-nav]') as HTMLElement | null;
      const top = this.top;
      if (t && top && top.root.contains(t) && t !== this.focused) this.setFocus(t, false);
    });
    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      this.pollPad();
    };
    this.rafId = requestAnimationFrame(loop);
  }

  get top(): NavLayer | undefined {
    return this.layers[this.layers.length - 1];
  }

  push(layer: NavLayer, focusFirst = true): void {
    // Buttons already held (e.g. the Start press that opened this menu) must not re-trigger.
    this.snapshotPad();
    this.layers = this.layers.filter((l) => l.root !== layer.root);
    this.layers.push(layer);
    this.clearFocus();
    if (focusFirst) requestAnimationFrame(() => this.focusFirst());
  }

  remove(root: HTMLElement): void {
    const wasTop = this.top?.root === root;
    this.layers = this.layers.filter((l) => l.root !== root);
    if (wasTop) {
      this.clearFocus();
      requestAnimationFrame(() => this.focusFirst());
    }
  }

  clear(): void {
    this.layers = [];
    this.clearFocus();
  }

  focusFirst(): void {
    const items = this.items();
    const pref = items.find((i) => i.dataset.navDefault !== undefined) ?? items[0];
    if (pref) this.setFocus(pref, false);
  }

  private items(): HTMLElement[] {
    const top = this.top;
    if (!top) return [];
    return [...top.root.querySelectorAll<HTMLElement>('[data-nav]')].filter(visible);
  }

  private clearFocus(): void {
    this.focused?.classList.remove('focus');
    this.focused = undefined;
  }

  setFocus(e: HTMLElement, sound = true): void {
    if (this.focused === e) return;
    this.focused?.classList.remove('focus');
    this.focused = e;
    e.classList.add('focus');
    if (sound) {
      this.onMove?.();
      e.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
    e.dispatchEvent(new CustomEvent('navfocus', { bubbles: true }));
  }

  private move(dir: Dir): void {
    const items = this.items();
    if (!items.length) return;
    const cur = this.focused && items.includes(this.focused) ? this.focused : undefined;
    if (!cur) return this.setFocus(items[0]);
    const cr = cur.getBoundingClientRect();
    const cx = cr.left + cr.width / 2;
    const cy = cr.top + cr.height / 2;
    let best: HTMLElement | undefined;
    let bestScore = Infinity;
    for (const it of items) {
      if (it === cur) continue;
      const r = it.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const dx = x - cx;
      const dy = y - cy;
      let main = 0;
      let cross = 0;
      if (dir === 'up') [main, cross] = [-dy, dx];
      if (dir === 'down') [main, cross] = [dy, dx];
      if (dir === 'left') [main, cross] = [-dx, dy];
      if (dir === 'right') [main, cross] = [dx, dy];
      if (main <= 4) continue;
      const score = main + Math.abs(cross) * 2.2;
      if (score < bestScore) {
        bestScore = score;
        best = it;
      }
    }
    if (best) this.setFocus(best);
    else if (dir === 'left' || dir === 'right') this.adjust(cur, dir === 'right' ? 1 : -1);
  }

  /** Left/right on a slider or segmented row adjusts it. Returns true if handled. */
  private adjust(e: HTMLElement, sign: number): boolean {
    const range = e.querySelector<HTMLInputElement>('input[type=range]');
    if (range) {
      const step = Number(range.step) || 0.05;
      const v = Math.min(Number(range.max), Math.max(Number(range.min), Number(range.value) + step * sign * 2));
      range.value = String(v);
      range.dispatchEvent(new Event('input', { bubbles: true }));
      this.onMove?.();
      return true;
    }
    const seg = e.querySelector('.seg');
    if (seg) {
      const bs = [...seg.querySelectorAll<HTMLButtonElement>('button')];
      const i = bs.findIndex((b) => b.classList.contains('active'));
      const n = bs[Math.min(bs.length - 1, Math.max(0, i + sign))];
      if (n && n !== bs[i]) n.click();
      return true;
    }
    return false;
  }

  private activate(): void {
    const f = this.focused;
    if (!f || !this.items().includes(f)) return this.focusFirst();
    this.onActivate?.();
    const toggle = f.querySelector<HTMLInputElement>('.toggle input');
    if (toggle) toggle.click();
    else f.click();
  }

  private back(): void {
    this.top?.onBack?.();
  }

  private onKey = (e: KeyboardEvent): void => {
    const top = this.top;
    if (!top || this.suspended) return;
    const code = e.code;
    const map: Record<string, Dir> = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', KeyW: 'up', KeyS: 'down', KeyA: 'left', KeyD: 'right' };
    if (map[code]) {
      e.preventDefault();
      const d = map[code];
      // Sliders: left/right adjusts rather than moving focus.
      if ((d === 'left' || d === 'right') && this.focused && this.adjust(this.focused, d === 'right' ? 1 : -1)) return;
      this.move(d);
    } else if (code === 'Enter' || code === 'Space' || code === 'NumpadEnter') {
      if (!this.focused) return;
      e.preventDefault();
      e.stopPropagation();
      this.activate();
    } else if (code === 'Escape' || code === 'Backspace') {
      if (top.onBack) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.back();
      }
    } else if (code === 'Tab') {
      e.preventDefault();
      const items = this.items();
      if (!items.length) return;
      const i = this.focused ? items.indexOf(this.focused) : -1;
      this.setFocus(items[(i + (e.shiftKey ? -1 : 1) + items.length) % items.length]);
    }
  };

  private snapshotPad(): void {
    if (typeof navigator.getGamepads !== 'function') return;
    for (const p of navigator.getGamepads()) if (p && p.connected) this.padPrev = p.buttons.map((x) => x.pressed);
  }

  private pollPad(): void {
    if (typeof navigator.getGamepads !== 'function') return;
    let pad: Gamepad | null = null;
    for (const p of navigator.getGamepads()) if (p && p.connected) pad = p;
    if (!pad) return;
    if (!this.top || this.suspended) {
      this.padPrev = pad.buttons.map((x) => x.pressed);
      return;
    }
    const b = (i: number) => !!pad!.buttons[i]?.pressed;
    const pressed = (i: number) => b(i) && !this.padPrev[i];
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    let dir: Dir | '' = '';
    if (b(12) || ay < -0.55) dir = 'up';
    else if (b(13) || ay > 0.55) dir = 'down';
    else if (b(14) || ax < -0.55) dir = 'left';
    else if (b(15) || ax > 0.55) dir = 'right';
    const now = performance.now();
    if (dir) {
      if (dir !== this.padRepeat.dir || now >= this.padRepeat.next) {
        const first = dir !== this.padRepeat.dir;
        this.padRepeat = { dir, next: now + (first ? 380 : 120) };
        this.onGamepad?.();
        if ((dir === 'left' || dir === 'right') && this.focused && this.adjust(this.focused, dir === 'right' ? 1 : -1)) {
          /* adjusted */
        } else this.move(dir);
      }
    } else this.padRepeat.dir = '';
    if (pressed(0)) {
      this.onGamepad?.();
      this.activate();
    }
    if (pressed(1)) this.back();
    if (pressed(9)) this.top.onStart ? this.top.onStart() : this.top.onBack?.();
    this.padPrev = pad.buttons.map((x) => x.pressed);
  }

  dispose(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('keydown', this.onKey, true);
  }
}

/** Build a nav-enabled button. */
export function navButton(label: string, cls = '', onClick?: () => void): HTMLButtonElement {
  const b = el('button', `btn ${cls}`);
  b.dataset.nav = '';
  b.innerHTML = label;
  if (onClick) b.addEventListener('click', onClick);
  return b;
}
